import {EditorTool, EditorToolType, ExtendedEditorToolDefinition, ExtendedEditorToolType, GroupFeaturePointer} from '@mc/react-mapio';
import {LayerVO} from '../../api';

export const ToolbarGroupId = {
    ACTIONS: 'action-group',
    COLLECTION_ADD: 'collection-add-tools',
    COLLECTION_MANAGE: 'collection-manage-tools',
    COLLECTION_CUTOUT: 'collection-cutout-tools',
    MENU_NEW: 'menu-new',
    SELECT_MENU: 'select-menu',
    SELECT_GROUP: 'select-group',
    MEASURE_MENU: 'measure-menu',
} as const;
export type ToolbarGroupId = (typeof ToolbarGroupId)[keyof typeof ToolbarGroupId];

// Všechny existující nástroje výběru
const SELECTION_TOOLS = new Set<EditorTool>([
    EditorTool.SELECT,
    EditorTool.EXACT_SELECT,
    EditorTool.EXACT_SINGLE_SELECT,
    EditorTool.BOX_SELECT,
    EditorTool.POLY_SELECT,
    EditorTool.CIRCLE_SELECT,
    EditorTool.LINE_SELECT,
    EditorTool.POLY_SELECT_INTERSECT,
]);

const shouldDisplayActions = (featurePointers: GroupFeaturePointer[], layerData?: {[key: string]: LayerVO}, serverPublic?: boolean): boolean => {
    if (serverPublic) {
        return false;
    }
    if ((!featurePointers || featurePointers?.length === 0) && !layerData) {
        return false;
    }

    return featurePointers.some(item => layerData?.[item?.layerId]?.editable);
};

/**
 * Pokud vstupní nástroje obsahují nástroj(e) pro výběr a současně je vybrána alespoň jedna feature, doplní k nástrojům
 * editační akce pro vybrané feature. Vstupní nástroje jsou modifikovány in-place.
 *
 * @param tools vstupní nástroje
 * @param featurePointers pointery vybraných feature
 * @param layerData definice dostupných vrstev
 * @param serverPublic příznak, zda Mapio běží ve veřejném režimu
 * @param podporaKolekci příznak, zda jsou povoleny nástroje pro správu kolekcí
 */
export const addEditTools = (
    tools: (EditorTool | ExtendedEditorToolDefinition)[],
    featurePointers: GroupFeaturePointer[],
    layerData?: {[key: string]: LayerVO},
    serverPublic?: boolean,
    podporaKolekci?: boolean,
): (EditorTool | ExtendedEditorToolDefinition)[] => {
    const processTools = (toolsToProcess: (EditorTool | ExtendedEditorToolDefinition)[]): void => {
        for (const tool of toolsToProcess) {
            // Rekurzivně zpracuj vnořené items
            if (typeof tool === 'object' && 'items' in tool && tool.items) {
                processTools(tool.items);
            }

            // Hledej menu obsahující nástroje výběru
            if (
                typeof tool === 'object' &&
                tool.type === EditorToolType.MENU &&
                'items' in tool &&
                tool.items.some(i => typeof i === 'object' && i.items?.some(t => typeof t === 'string' && SELECTION_TOOLS.has(t as EditorTool)))
            ) {
                // Najdi všechny indexy editačních bloků
                const editToolGroupIds = new Set<ToolbarGroupId>([
                    ToolbarGroupId.ACTIONS,
                    ToolbarGroupId.COLLECTION_ADD,
                    ToolbarGroupId.COLLECTION_MANAGE,
                    ToolbarGroupId.COLLECTION_CUTOUT,
                ]);
                const indicesToRemove = new Set<number>();

                tool.items.forEach((item, index) => {
                    if (typeof item === 'object' && item.id && editToolGroupIds.has(item.id as ToolbarGroupId)) {
                        indicesToRemove.add(index);
                        // Pokud bezprostředně před tímto blokem je separátor, zahrň ho také
                        if (index > 0 && tool.items[index - 1] === EditorTool.SEPARATOR) {
                            indicesToRemove.add(index - 1);
                        }
                    }
                });

                // Odstraň prvky sestupně podle indexu (aby se indexy neposouvaly)
                for (const index of Array.from(indicesToRemove).sort((a, b) => b - a)) {
                    tool.items.splice(index, 1);
                }

                // Přidej nové editační nástroje, pokud je potřeba
                if (shouldDisplayActions(featurePointers, layerData, serverPublic)) {
                    tool.items = [...tool.items, EditorTool.SEPARATOR, ...selectedActionsToolbarDef(featurePointers, layerData, podporaKolekci)];
                }
            }
        }
    };

    processTools(tools);
    return tools;
};

/**
 * Položky skupiny "Přidat / Přidání do kolekce" – tvořicí nástroje filtrované podle typu geometrie.
 * Jediný zdroj pravdy pro obě varianty (existující kolekce i jednotlivý objekt), aby nabídka byla totožná
 * a udržovala se na jednom místě. "Nový" (ADD_TO_COLLECTION) = volné zakreslení, "Existující"
 * (MULTI_ADD_EXISTING) = převzetí existujícího objektu. Nad jednotlivým objektem engine zákres přidá
 * do vybrané feature a povýší ji na kolekci (viz příznak addToCollection v ToolbarDetail / ToolActions).
 */
const collectionAddGroupItems = (allowPoint: boolean, allowLine: boolean, allowPoly: boolean): EditorTool[] => [
    EditorTool.ADD_TO_COLLECTION,
    EditorTool.MULTI_ADD_EXISTING,
    ...(allowPoint ? [EditorTool.POINT_COORDS] : []),
    ...(allowLine ? [EditorTool.LINE_COORDS, EditorTool.LINE_OFFSET, EditorTool.PERPENDICULAR_LINE, EditorTool.GUIDED_LINE] : []),
    ...(allowPoly
        ? [EditorTool.MULTI_FIXED_RECTANGLE, EditorTool.POLYGON_COORDS, EditorTool.CIRCLE_COORDS, EditorTool.CIRCLE_RADIUS, EditorTool.GUIDED_POLYGON]
        : []),
];

export const selectedActionsToolbarDef = (
    selectedFeatures: GroupFeaturePointer[],
    layerData: {[key: string]: LayerVO},
    podporaKolekci?: boolean,
): (EditorTool | ExtendedEditorToolDefinition)[] => {
    let allowLine = true;
    let allowPoly = true;
    let allowPoint = true;

    let isMultiGeometry = false;
    //mají všechny vrstvy práva?
    if (layerData) {
        for (const feature of selectedFeatures) {
            const layer = layerData[feature.layerId];
            if (!layer) {
                continue;
            }
            const allowedGeometry = layer.allowedGeometry;

            if (allowedGeometry) {
                if (!allowedGeometry.includes('LINE')) allowLine = false;
                if (!allowedGeometry.includes('POLYGON')) allowPoly = false;
                if (!allowedGeometry.includes('POINT')) allowPoint = false;
            }

            if (feature.isMultiGeometry) {
                isMultiGeometry = true;
            }
        }
    }
    const items: (EditorTool | ExtendedEditorToolDefinition)[] = [];
    const editItems: (EditorTool | ExtendedEditorToolDefinition)[] = [
        EditorTool.EDIT,
        EditorTool.MOVE,
        ...(allowLine || allowPoly ? [EditorTool.ROTATE, EditorTool.OFFSET] : []),
        ...(allowLine ? [EditorTool.LINE_EXTENSION] : []),
        EditorTool.COPY,
        ...(allowLine && !isMultiGeometry ? [EditorTool.MERGE_LINES, EditorTool.SPLIT_LINE] : []),
        ...(allowPoly && !isMultiGeometry ? [EditorTool.SPLIT_POLYGON] : []),
        ...(isMultiGeometry && podporaKolekci !== false ? [EditorTool.REMOVE_FROM_COLLECTION] : []),
        EditorTool.DELETE,
    ];

    // Přidáme nástroje pro multi-geometrie
    if (isMultiGeometry) {
        return getMultiGeometryTools(allowPoly, editItems, podporaKolekci, allowLine, allowPoint);
    } else {
        items.push(...editItems);
    }

    return [
        {
            id: ToolbarGroupId.ACTIONS,
            type: ExtendedEditorToolType.GROUP,
            name: 'Akce nad vybranými objekty',
            rememberSecondary: true,
            horizontal: true,
            items,
        } as ExtendedEditorToolDefinition,
        ...(podporaKolekci !== false
            ? [
                  EditorTool.SEPARATOR,
                  {
                      id: ToolbarGroupId.COLLECTION_ADD,
                      type: ExtendedEditorToolType.GROUP,
                      name: 'Přidání do kolekce',
                      rememberSecondary: true,
                      // Tato větev běží jen pro jednotlivý objekt (isMultiGeometry === false; multigeometrie
                      // se vrací dříve přes getMultiGeometryTools). Paleta je sdílená, viz collectionAddGroupItems.
                      items: collectionAddGroupItems(allowPoint, allowLine, allowPoly),
                  } as ExtendedEditorToolDefinition,
                  ...(allowPoly
                      ? [
                            EditorTool.SEPARATOR,
                            {
                                id: ToolbarGroupId.COLLECTION_CUTOUT,
                                type: ExtendedEditorToolType.GROUP,
                                name: 'Výřezy z kolekce',
                                rememberSecondary: true,
                                items: [EditorTool.CUTOUT_DRAW, EditorTool.CUTOUT_FIXED_RECTANGLE, EditorTool.CUTOUT_FROM_EXISTING, EditorTool.CUTOUT_REMOVE],
                            } as ExtendedEditorToolDefinition,
                        ]
                      : []),
              ]
            : []),
    ];
};

const getMultiGeometryTools = (
    allowPoly: boolean,
    editItems: (EditorTool | ExtendedEditorToolDefinition)[],
    podporaKolekci?: boolean,
    allowLine = true,
    allowPoint = true,
): (EditorTool | ExtendedEditorToolDefinition)[] => {
    if (podporaKolekci === false) {
        // Pokud nejsou kolekce podporovány, vrátíme pouze editační nástroje obalené do skupiny ACTIONS
        return [
            {
                id: ToolbarGroupId.ACTIONS,
                type: ExtendedEditorToolType.GROUP,
                name: 'Akce nad vybranými objekty',
                rememberSecondary: true,
                horizontal: true,
                items: editItems,
            } as ExtendedEditorToolDefinition,
        ];
    }
    return [
        {
            id: ToolbarGroupId.COLLECTION_ADD,
            type: ExtendedEditorToolType.GROUP,
            name: 'Přidat do kolekce',
            rememberSecondary: true,
            // Sdílená paleta tvořicích nástrojů, viz collectionAddGroupItems (jediný zdroj pravdy).
            items: collectionAddGroupItems(allowPoint, allowLine, allowPoly),
        } as ExtendedEditorToolDefinition,
        EditorTool.SEPARATOR,
        {
            id: ToolbarGroupId.COLLECTION_MANAGE,
            type: ExtendedEditorToolType.GROUP,
            name: 'Manipulace se zákresem',
            rememberSecondary: true,
            horizontal: true,
            items: editItems,
        } as ExtendedEditorToolDefinition,
        ...(allowPoly
            ? [
                  EditorTool.SEPARATOR,
                  {
                      id: ToolbarGroupId.COLLECTION_CUTOUT,
                      type: ExtendedEditorToolType.GROUP,
                      name: 'Výřezy z kolekce',
                      rememberSecondary: true,
                      items: [EditorTool.CUTOUT_DRAW, EditorTool.CUTOUT_FIXED_RECTANGLE, EditorTool.CUTOUT_FROM_EXISTING, EditorTool.CUTOUT_REMOVE],
                  } as ExtendedEditorToolDefinition,
              ]
            : []),
    ];
};

export const newItemToolbarChildDef: EditorToolType[] = [
    EditorToolType.POINT,
    EditorToolType.POINT_COORDS,
    EditorToolType.LINE,
    EditorToolType.LINE_COORDS,
    EditorToolType.LINE_OFFSET,
    EditorToolType.PERPENDICULAR_LINE,
    EditorToolType.POLYGON,
    EditorToolType.FIXED_RECTANGLE,
    EditorToolType.CIRCLE_COORDS,
    EditorToolType.CIRCLE_RADIUS,
    EditorToolType.GUIDED_LINE,
    EditorToolType.GUIDED_POLYGON,
];

/**
 * Vrátí výchozí nástroje, použité v případě, kdy nadřazená aplikace žádné nástroje nepředala
 * a Mapio běží v interním režimu.
 *
 * @param selectedFeaturePointers pointery na aktuálně vybrané features
 * @param layerData konfigurace dostupných vrstev
 */
export const toolbarFullDef = (
    selectedFeaturePointers: GroupFeaturePointer[],
    layerData: {[key: string]: LayerVO},
    podporaKolekci?: boolean,
): (EditorTool | ExtendedEditorToolDefinition)[] => {
    const tools: (EditorTool | ExtendedEditorToolDefinition)[] = [
        EditorTool.ORIGIN_LOCATION,
        EditorTool.SHOW_COORDS,
        {
            id: ToolbarGroupId.MEASURE_MENU,
            type: EditorToolType.MENU,
            icon: 'mcRules',
            name: 'Nástroj pro měření',
            horizontal: true,
            items: [
                {
                    id: 'measure-group',
                    type: ExtendedEditorToolType.GROUP,
                    name: 'Měření',
                    items: [EditorTool.MEASURE_LINE, EditorTool.MEASURE_POLYGON],
                } as ExtendedEditorToolDefinition,
            ],
            order: 2,
            section: 0,
        } as ExtendedEditorToolDefinition,
        EditorTool.SEPARATOR,
        {
            // POZOR - pokud se změní zanoření tohoto menu NEBO jeho items, je potřeba přetestovat
            // nebo upravit funkčnost enhanceSelectionTools, aby správně přidávala editační akce
            id: ToolbarGroupId.SELECT_MENU,
            type: EditorToolType.MENU,
            icon: 'mcSelect',
            name: 'Nástroj pro výběr',
            horizontal: true,
            items: [
                {
                    id: ToolbarGroupId.SELECT_GROUP,
                    type: ExtendedEditorToolType.GROUP,
                    name: 'Výběr',
                    horizontal: !selectedFeaturePointers?.length,
                    items: [EditorTool.EXACT_SELECT, EditorTool.BOX_SELECT, EditorTool.POLY_SELECT],
                } as ExtendedEditorToolDefinition,
            ],
            order: 3,
            section: 1,
        } as ExtendedEditorToolDefinition,
        {
            id: ToolbarGroupId.MENU_NEW,
            type: EditorToolType.MENU,
            icon: 'mcNew',
            name: 'Nový objekt',
            horizontal: true,
            items: [
                {
                    id: 'z-1',
                    type: ExtendedEditorToolType.NEW_ITEM,
                    name: 'Zakreslit',
                } as ExtendedEditorToolDefinition,
            ],
            order: 4,
            section: 1,
        } as ExtendedEditorToolDefinition,
        EditorTool.SEPARATOR,
        {
            id: EditorToolType.SNAP,
            type: EditorToolType.SNAP,
            icon: 'mcAttachementNonactive',
            iconActive: 'mcAttachementActive',
            name: 'Přichytávání',
            order: 5,
            section: 2,
        } as ExtendedEditorToolDefinition,
        {
            id: 'menu-other',
            type: EditorToolType.MENU,
            icon: 'mcExport',
            name: 'Ostatní',
            items: [
                // Takto zvlášť je to jen kvůli trochu jinému názvu
                {id: 'EXPORT_LAYERS', type: EditorToolType.EXPORT_LAYERS, name: 'Export vrstev', icon: 'mcExportLayers'} as ExtendedEditorToolDefinition,
                // {id: 'EXPORT_FEATURES', type: EditorToolType.EXPORT_FEATURES, name: 'Export objektů', icon: 'mcExportLayers'},
                EditorTool.PRINT,
            ],
            order: 6,
            section: 2,
        } as ExtendedEditorToolDefinition,
        EditorTool.INSPECT,
    ];
    return addEditTools(tools, selectedFeaturePointers, layerData, undefined, podporaKolekci);
};

/**
 * Vrátí výchozí nástroje, použité v případě, kdy nadřazená aplikace žádné nástroje nepředala
 * a Mapio běží ve veřejném režimu (tj. pouze "pasivní" nástroje).
 */
export const toolbarPublicDef = (): (EditorTool | ExtendedEditorToolDefinition)[] => {
    return [
        EditorTool.ORIGIN_LOCATION,
        EditorTool.SHOW_COORDS,
        {
            id: ToolbarGroupId.MEASURE_MENU,
            type: EditorToolType.MENU,
            icon: 'mcRules',
            name: 'Nástroj pro měření',
            horizontal: true,
            items: [
                {
                    id: 'measure-group',
                    type: ExtendedEditorToolType.GROUP,
                    name: 'Měření',
                    items: [EditorTool.MEASURE_LINE, EditorTool.MEASURE_POLYGON],
                } as ExtendedEditorToolDefinition,
            ],
            order: 2,
            section: 0,
        } as ExtendedEditorToolDefinition,
        EditorTool.SEPARATOR,
        {
            id: ToolbarGroupId.SELECT_MENU,
            type: EditorToolType.MENU,
            icon: 'mcSelect',
            name: 'Nástroj pro výběr',
            horizontal: true,
            items: [
                {
                    id: ToolbarGroupId.SELECT_GROUP,
                    type: ExtendedEditorToolType.GROUP,
                    name: 'Výběr',
                    items: [EditorTool.SELECT, EditorTool.BOX_SELECT, EditorTool.POLY_SELECT],
                } as ExtendedEditorToolDefinition,
            ],
            order: 3,
            section: 1,
        } as ExtendedEditorToolDefinition,

        // Takto zvlášť je to jen kvůli trochu jinému názvu
        {id: 'EXPORT_LAYERS', type: EditorToolType.EXPORT_LAYERS, name: 'Export vrstev', icon: 'mcExportLayers'} as ExtendedEditorToolDefinition,
        // {id: 'EXPORT_FEATURES', type: EditorToolType.EXPORT_FEATURES, name: 'Export objektů', icon: 'mcExportLayers'},
        // EditorTool.PRINT,
    ];
};
